import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Newspaper,
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  DollarSign,
  BarChart3,
  Target,
  Loader2,
  Info,
  Zap,
  CheckCircle,
  XCircle,
  Minus,
  Bell,
  Lock,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { NEWS_ECO_SLIDES } from '../config/tutorialSlides';
import HowItWorks from '../components/HowItWorks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert New York time to local time
const convertNYTimeToLocal = (timeStr, dateStr) => {
  if (!timeStr || timeStr === 'TBD' || timeStr === '—') {
    return { local: timeStr, ny: timeStr };
  }
  
  try {
    // Parse the time (format: "14:30" or "2:30 PM")
    let hours, minutes;
    
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      // 12-hour format
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        hours = parseInt(match[1]);
        minutes = parseInt(match[2]);
        const isPM = match[3].toUpperCase() === 'PM';
        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;
      } else {
        return { local: timeStr, ny: timeStr };
      }
    } else {
      // 24-hour format
      const parts = timeStr.split(':');
      hours = parseInt(parts[0]);
      minutes = parseInt(parts[1]) || 0;
    }
    
    // Create a date in New York timezone
    // Use current date if no date provided
    const today = new Date();
    let year = today.getFullYear();
    let month = today.getMonth();
    let day = today.getDate();
    
    if (dateStr) {
      // Try to parse date (format: "dd/mm/yyyy" or similar)
      const dateParts = dateStr.split('/');
      if (dateParts.length >= 2) {
        day = parseInt(dateParts[0]);
        month = parseInt(dateParts[1]) - 1;
        if (dateParts[2]) year = parseInt(dateParts[2]);
      }
    }
    
    // Create date string in NY timezone
    const nyDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    
    // Create date assuming NY time
    const nyDate = new Date(nyDateStr);
    
    // Get NY timezone offset (accounting for DST)
    const nyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const localFormatter = new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    // Calculate the offset between NY and UTC
    const nyOffset = -5; // EST (simplified, DST handled below)
    const now = new Date();
    const nyNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const utcNow = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const actualNyOffset = (utcNow - nyNow) / (1000 * 60 * 60);
    
    // Convert NY time to UTC
    const utcHours = hours + actualNyOffset;
    
    // Get local timezone offset
    const localOffset = -now.getTimezoneOffset() / 60;
    
    // Calculate local time
    let localHours = utcHours + localOffset;
    
    // Handle day overflow
    if (localHours >= 24) localHours -= 24;
    if (localHours < 0) localHours += 24;
    
    const localTimeStr = `${String(Math.floor(localHours)).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    const nyTimeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    
    return {
      local: localTimeStr,
      ny: nyTimeStr
    };
  } catch (e) {
    console.error('Time conversion error:', e);
    return { local: timeStr, ny: timeStr };
  }
};

// Format time display with both local and NY time
const TimeDisplay = ({ time, date, showNY = true }) => {
  const { local, ny } = convertNYTimeToLocal(time, date);
  
  if (!time || time === 'TBD' || time === '—') {
    return <span className="text-muted-foreground">TBD</span>;
  }
  
  // Check if times are the same (same timezone)
  const isSameTime = local === ny;
  
  return (
    <span className="flex items-center gap-1">
      <span className="font-medium">{local}</span>
      {showNY && !isSameTime && (
        <span className="text-xs text-muted-foreground">(NY: {ny})</span>
      )}
    </span>
  );
};

// Event type configurations
const EVENT_TYPES = {
  NFP: {
    name: "Non-Farm Payrolls",
    shortName: "NFP",
    description: "Rapport mensuel sur l'emploi américain. Impact majeur sur USD et marchés.",
    color: "bg-red-500",
    textColor: "text-red-400",
    borderColor: "border-red-500/30",
    bgColor: "bg-red-500/10",
    icon: BarChart3
  },
  CPI: {
    name: "Consumer Price Index",
    shortName: "CPI",
    description: "Mesure de l'inflation. Influence directe sur les décisions de la Fed.",
    color: "bg-orange-500",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/30",
    bgColor: "bg-orange-500/10",
    icon: TrendingUp
  },
  PPI: {
    name: "Producer Price Index",
    shortName: "PPI",
    description: "Inflation au niveau des producteurs. Indicateur avancé du CPI.",
    color: "bg-yellow-500",
    textColor: "text-yellow-400",
    borderColor: "border-yellow-500/30",
    bgColor: "bg-yellow-500/10",
    icon: TrendingUp
  },
  FOMC: {
    name: "Fed Meeting / FOMC",
    shortName: "FOMC",
    description: "Décisions sur les taux d'intérêt. L'événement le plus important.",
    color: "bg-purple-500",
    textColor: "text-purple-400",
    borderColor: "border-purple-500/30",
    bgColor: "bg-purple-500/10",
    icon: Target
  },
  PCE: {
    name: "Personal Consumption Expenditures",
    shortName: "PCE",
    description: "Mesure d'inflation préférée de la Fed. Impact fort sur les marchés.",
    color: "bg-blue-500",
    textColor: "text-blue-400",
    borderColor: "border-blue-500/30",
    bgColor: "bg-blue-500/10",
    icon: DollarSign
  }
};

// Impact badge component
const ImpactBadge = ({ impact }) => {
  const config = {
    high: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Fort" },
    medium: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Moyen" },
    low: { color: "bg-green-500/20 text-green-400 border-green-500/30", label: "Faible" }
  };
  
  const cfg = config[impact] || config.low;
  
  return (
    <Badge variant="outline" className={`${cfg.color} text-xs`}>
      {cfg.label}
    </Badge>
  );
};

// Event card component for key events
const KeyEventCard = ({ type, events }) => {
  const config = EVENT_TYPES[type];
  if (!config) return null;
  
  const Icon = config.icon;
  const nextEvent = events && events.length > 0 ? events[0] : null;
  
  return (
    <Card className={`${config.bgColor} ${config.borderColor} border`}>
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-lg ${config.textColor}`}>
          <div className={`p-2 rounded-lg ${config.color}/20`}>
            <Icon className="w-5 h-5" />
          </div>
          {config.shortName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {nextEvent ? (
          <div className="space-y-3">
            {/* Next event date */}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold">{nextEvent.date}</span>
              <Clock className="w-4 h-4 text-muted-foreground ml-2" />
              <TimeDisplay time={nextEvent.time} date={nextEvent.date} />
            </div>
            
            {/* Event name */}
            <p className="text-sm text-muted-foreground">{nextEvent.event}</p>
            
            {/* Forecast vs Previous */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="p-2 bg-background/50 rounded">
                <div className="text-xs text-muted-foreground">Prévision</div>
                <div className={`font-bold ${config.textColor}`}>
                  {nextEvent.forecast || "—"}
                </div>
              </div>
              <div className="p-2 bg-background/50 rounded">
                <div className="text-xs text-muted-foreground">Précédent</div>
                <div className="font-bold text-muted-foreground">
                  {nextEvent.previous || "—"}
                </div>
              </div>
            </div>
            
            {/* Actual if available */}
            {nextEvent.actual && (
              <div className="p-2 bg-green-500/10 rounded border border-green-500/20">
                <div className="text-xs text-green-400">Résultat</div>
                <div className="font-bold text-green-400">{nextEvent.actual}</div>
              </div>
            )}
            
            {/* Description */}
            <p className="text-xs text-muted-foreground/70 mt-2">
              {config.description}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground/50">
              <Calendar className="w-4 h-4" />
              <span>Pas de date prévue</span>
            </div>
            <p className="text-xs text-muted-foreground/50">
              Aucun événement {config.shortName} dans les prochaines semaines.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-2">
              {config.description}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Event row for table
const EventRow = ({ event }) => {
  const isKeyEvent = event.is_key_event;
  
  return (
    <tr className={`border-b border-border/30 hover:bg-muted/20 transition-colors ${isKeyEvent ? 'bg-primary/5' : ''}`}>
      <td className="py-3 px-4">
        <div className="flex flex-col gap-1">
          <span className="font-medium">{event.date}</span>
          {event.time && (
            <TimeDisplay time={event.time} date={event.date} />
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <Badge variant="outline" className="text-xs">
          {event.currency}
        </Badge>
      </td>
      <td className="py-3 px-4">
        <ImpactBadge impact={event.impact} />
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          {isKeyEvent && <Zap className="w-4 h-4 text-yellow-400" />}
          <span className={isKeyEvent ? 'font-semibold' : ''}>{event.event}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-center">
        <span className="text-primary font-medium">{event.forecast || "—"}</span>
      </td>
      <td className="py-3 px-4 text-center text-muted-foreground">
        {event.previous || "—"}
      </td>
      <td className="py-3 px-4 text-center">
        {event.actual ? (
          <span className="text-green-400 font-bold">{event.actual}</span>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </td>
    </tr>
  );
};

const NewsEco = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [groupedEvents, setGroupedEvents] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/economic-calendar/key-events`);
      setEvents(response.data.all_key_events || []);
      setGroupedEvents(response.data.grouped_events || {});
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      console.error('Failed to fetch economic calendar:', error);
      toast.error('Erreur lors du chargement du calendrier économique');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const response = await axios.get(`${API}/economic-calendar/results`);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch economic results:', error);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendar();
    fetchResults();
    // Refresh every 5 minutes
    const interval = setInterval(() => {
      fetchCalendar();
      fetchResults();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchCalendar, fetchResults]);

  const formatLastUpdated = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Get user's timezone name
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'Local';
    }
  };

  // Guest gate overlay (same pattern as Calculator.js)
  const GuestGate = ({ title, body }) => (
    <div
      className="absolute inset-0 z-20 flex items-start justify-center pt-24 p-4"
      data-testid="newseco-gate-overlay"
    >
      <Card className="max-w-md w-full bg-card/95 backdrop-blur-md border-primary/30 shadow-2xl sticky top-32">
        <CardContent className="p-6 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-xl font-bold flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            {title}
          </h3>
          <p className="text-sm text-muted-foreground">{body}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Link to="/register" className="flex-1">
              <Button className="w-full bg-primary hover:bg-primary/90 font-bold" data-testid="newseco-gate-register-btn">
                {language === 'fr' ? 'Créer un compte gratuit' : 'Create a free account'}
              </Button>
            </Link>
            <Link to="/login" className="flex-1">
              <Button variant="outline" className="w-full border-white/20" data-testid="newseco-gate-login-btn">
                {language === 'fr' ? 'Se connecter' : 'Log in'}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="news-eco-page">
      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        pageId="news-eco"
        slides={NEWS_ECO_SLIDES}
        storageKey="tutorial_news_eco_seen"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Newspaper className="w-8 h-8 text-primary" />
              Calendrier Économique
            </h1>
            <HowItWorks pageKey="news-eco" />
          </div>
          <p className="text-muted-foreground">
            Événements majeurs qui impactent les marchés • NFP, CPI, PPI, FOMC, PCE
          </p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Heures affichées en {getUserTimezone()} (heure de New York entre parenthèses)
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Mis à jour: {formatLastUpdated(lastUpdated)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCalendar}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('https://www.forexfactory.com/calendar', '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Forex Factory
          </Button>
        </div>
      </div>

      {/* Warning banner */}
      <Card className="bg-red-500/10 border-red-500/20 mb-4">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-400 mb-1">Important pour l'Intraday</h3>
              <p className="text-sm text-muted-foreground">
                Le marché va <strong>pricer ces prises de paroles</strong>. Évitez d'avoir des positions ouvertes 
                pendant ces événements ou réduisez votre taille de position. La volatilité peut être extrême 
                (1-3% en quelques minutes).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timezone info banner */}
      <Card className="bg-blue-500/10 border-blue-500/20 mb-6">
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div className="text-sm">
              <span className="text-blue-400 font-semibold">Fuseau horaire :</span>{' '}
              <span className="text-muted-foreground">
                Toutes les heures sont en <strong className="text-blue-400">heure de New York (ET)</strong> — 
                Pour convertir en heure de Paris, ajoutez <strong>+6h</strong> (heure d'hiver) ou <strong>+5h</strong> (heure d'été).
                Exemple : 8:30 AM ET = 14:30 heure de Paris (été) / 15:30 (hiver)
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-6 w-[450px]">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="all">Tous les événements</TabsTrigger>
            <TabsTrigger value="results" className="relative">
              Résultats
              {results.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
                  {results.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab - Key Events Cards */}
          <TabsContent value="overview" className="mt-0">
            <div className={!isAuthenticated ? 'relative' : ''}>
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8 ${!isAuthenticated ? 'blur-sm pointer-events-none select-none' : ''}`} aria-hidden={!isAuthenticated}>
                <KeyEventCard type="NFP" events={groupedEvents.NFP} />
                <KeyEventCard type="CPI" events={groupedEvents.CPI} />
                <KeyEventCard type="PPI" events={groupedEvents.PPI} />
                <KeyEventCard type="FOMC" events={groupedEvents.FOMC} />
                <KeyEventCard type="PCE" events={groupedEvents.PCE} />
              </div>
              {!isAuthenticated && (
                <GuestGate
                  title={language === 'fr' ? 'Événements réservés aux membres' : 'Events reserved for members'}
                  body={language === 'fr'
                    ? 'Créez un compte gratuit pour consulter les événements NFP, CPI, PPI, FOMC et PCE en temps réel, avec prévisions et impact sur les marchés.'
                    : 'Create a free account to see NFP, CPI, PPI, FOMC and PCE events in real time, with forecasts and market impact.'}
                />
              )}
            </div>

            {/* Explanation cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Comment interpréter les résultats ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400 mt-0.5" />
                    <div>
                      <strong className="text-green-400">Résultat &gt; Prévision</strong>
                      <p className="text-muted-foreground">
                        Souvent bullish pour USD (sauf pour l'inflation où c'est bearish pour les actifs risqués)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <TrendingDown className="w-4 h-4 text-red-400 mt-0.5" />
                    <div>
                      <strong className="text-red-400">Résultat &lt; Prévision</strong>
                      <p className="text-muted-foreground">
                        Souvent bearish pour USD, peut être bullish pour crypto/actions si ça implique des baisses de taux
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Horaires importants (Heure Paris)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 bg-muted/30 rounded">
                    <span>NFP / CPI / PPI / PCE</span>
                    <strong className="text-primary">14h30</strong>
                  </div>
                  <div className="flex justify-between p-2 bg-muted/30 rounded">
                    <span>FOMC Statement</span>
                    <strong className="text-primary">20h00</strong>
                  </div>
                  <div className="flex justify-between p-2 bg-muted/30 rounded">
                    <span>FOMC Press Conference</span>
                    <strong className="text-primary">20h30</strong>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* All Events Tab */}
          <TabsContent value="all" className="mt-0">
            <div className={!isAuthenticated ? 'relative' : ''}>
              <Card className={`bg-card/50 border-border/50 ${!isAuthenticated ? 'blur-sm pointer-events-none select-none' : ''}`} aria-hidden={!isAuthenticated}>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border text-left text-sm text-muted-foreground bg-muted/20">
                        <th className="py-3 px-4">Date & Heure</th>
                        <th className="py-3 px-4">Devise</th>
                        <th className="py-3 px-4">Impact</th>
                        <th className="py-3 px-4">Événement</th>
                        <th className="py-3 px-4 text-center">Prévision</th>
                        <th className="py-3 px-4 text-center">Précédent</th>
                        <th className="py-3 px-4 text-center">Résultat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.length > 0 ? (
                        events.map((event, index) => (
                          <EventRow key={index} event={event} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-muted-foreground">
                            Aucun événement à afficher
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
              {!isAuthenticated && (
                <GuestGate
                  title={language === 'fr' ? 'Calendrier complet réservé aux membres' : 'Full calendar for members'}
                  body={language === 'fr'
                    ? 'Créez un compte gratuit pour voir tous les événements économiques de la semaine, leurs prévisions, précédents et résultats.'
                    : 'Create a free account to see all economic events of the week, forecasts, previous values and results.'}
                />
              )}
            </div>
          </TabsContent>

          {/* Results Tab - Today's Published Results */}
          <TabsContent value="results" className="mt-0">
            <div className={!isAuthenticated ? 'relative' : ''}>
              <Card className={`bg-card/50 border-border/50 mb-6 ${!isAuthenticated ? 'blur-sm pointer-events-none select-none' : ''}`} aria-hidden={!isAuthenticated}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Résultats du jour
                </CardTitle>
              </CardHeader>
              <CardContent>
                {resultsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : results.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {results.map((result, index) => {
                      const config = EVENT_TYPES[result.event_type] || EVENT_TYPES.NFP;
                      const Icon = config?.icon || BarChart3;
                      
                      return (
                        <Card 
                          key={index} 
                          className={`${config?.bgColor || 'bg-primary/10'} ${config?.borderColor || 'border-primary/30'} border`}
                        >
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className={`p-2 rounded-lg ${config?.color || 'bg-primary'}/20`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <span className={`font-bold ${config?.textColor || 'text-primary'}`}>
                                  {result.event_type}
                                </span>
                              </div>
                              {result.beat_miss === 'beat' && (
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Supérieur
                                </Badge>
                              )}
                              {result.beat_miss === 'miss' && (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Inférieur
                                </Badge>
                              )}
                              {result.beat_miss === 'neutral' && (
                                <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                                  <Minus className="w-3 h-3 mr-1" />
                                  Conforme
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {result.event_name}
                            </p>
                            
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="p-2 bg-background/50 rounded">
                                <div className="text-xs text-muted-foreground">Résultat</div>
                                <div className={`font-bold text-lg ${
                                  result.beat_miss === 'beat' ? 'text-green-400' : 
                                  result.beat_miss === 'miss' ? 'text-red-400' : 'text-white'
                                }`}>
                                  {result.actual}
                                </div>
                              </div>
                              <div className="p-2 bg-background/50 rounded">
                                <div className="text-xs text-muted-foreground">Prévision</div>
                                <div className="font-semibold text-white">{result.forecast}</div>
                              </div>
                              <div className="p-2 bg-background/50 rounded">
                                <div className="text-xs text-muted-foreground">Précédent</div>
                                <div className="font-semibold text-muted-foreground">{result.previous}</div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              <TimeDisplay time={result.time} date={result.date} />
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Aucun résultat aujourd'hui</h3>
                    <p className="text-muted-foreground text-sm">
                      Les résultats NFP, CPI, PPI, FOMC et PCE publiés aujourd'hui apparaîtront ici.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info about results */}
            <Card className={`bg-blue-500/10 border-blue-500/20 ${!isAuthenticated ? 'blur-sm pointer-events-none select-none' : ''}`} aria-hidden={!isAuthenticated}>
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-blue-400 mb-1">À propos des résultats</h3>
                    <p className="text-sm text-muted-foreground">
                      Cette page affiche les résultats des indicateurs économiques majeurs publiés <strong>aujourd'hui</strong>.
                      Activez les notifications pour être alerté dès qu'un résultat est publié.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
              {!isAuthenticated && (
                <GuestGate
                  title={language === 'fr' ? 'Résultats réservés aux membres' : 'Results reserved for members'}
                  body={language === 'fr'
                    ? 'Créez un compte gratuit pour voir les résultats officiels des indicateurs économiques dès leur publication (NFP, CPI, PPI, FOMC, PCE).'
                    : 'Create a free account to see the official results of economic indicators as soon as they are published (NFP, CPI, PPI, FOMC, PCE).'}
                />
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default NewsEco;
